package com.zq.webproject.ui.bankcard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song


// 统一字体
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 添加银行卡界面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddBankCardScreen(
    onBackClick: () -> Unit,
    onAddSuccess: () -> Unit = {}
) {
    println("AddBankCardScreen - 渲染添加银行卡界面") // 日志输出

    // 创建ViewModel实例
    val viewModel = remember { AddBankCardViewModel() }

    // 表单输入状态
    var cardNumber by remember { mutableStateOf("") }
    var cardHolderName by remember { mutableStateOf("") }
    var expirationDate by remember { mutableStateOf("") }
    var cvv by remember { mutableStateOf("") }
    var bankName by remember { mutableStateOf("") }

    // 监听添加成功
    LaunchedEffect(viewModel.addSuccess) {
        if (viewModel.addSuccess) {
            println("AddBankCardScreen - 银行卡添加成功，延迟返回") // 日志输出
            kotlinx.coroutines.delay(1500)  // 延迟1.5秒显示成功提示
            onAddSuccess()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "添加银行卡",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("AddBankCardScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
                .verticalScroll(rememberScrollState())
        ) {
            Spacer(modifier = Modifier.height(16.dp))

            // 表单卡片
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp)
                ) {
                    // 银行名称
                    Text(
                        text = "银行名称",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                    OutlinedTextField(
                        value = bankName,
                        onValueChange = {
                            bankName = it
                            println("AddBankCardScreen - 输入银行名称: $it") // 日志输出
                        },
                        placeholder = {
                            Text(
                                text = "请输入银行名称（如：中国银行）",
                                fontFamily = CaiFont,
                                fontSize = 14.sp
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF1E88E5),
                            focusedLabelColor = Color(0xFF1E88E5)
                        )
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    // 持卡人姓名
                    Text(
                        text = "持卡人姓名",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                    OutlinedTextField(
                        value = cardHolderName,
                        onValueChange = {
                            cardHolderName = it
                            println("AddBankCardScreen - 输入持卡人姓名: $it") // 日志输出
                        },
                        placeholder = {
                            Text(
                                text = "请输入持卡人姓名",
                                fontFamily = CaiFont,
                                fontSize = 14.sp
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF1E88E5),
                            focusedLabelColor = Color(0xFF1E88E5)
                        )
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    // 银行卡号
                    Text(
                        text = "银行卡号",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                    OutlinedTextField(
                        value = cardNumber,
                        onValueChange = { newValue ->
                            // 只允许输入数字，最长19位
                            if (newValue.isEmpty() || (newValue.all { it.isDigit() } && newValue.length <= 19)) {
                                cardNumber = newValue
                                println("AddBankCardScreen - 输入银行卡号: $newValue") // 日志输出
                            }
                        },
                        placeholder = {
                            Text(
                                text = "请输入银行卡号",
                                fontFamily = CaiFont,
                                fontSize = 14.sp
                            )
                        },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF1E88E5),
                            focusedLabelColor = Color(0xFF1E88E5)
                        )
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    // 有效期和CVV在同一行
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // 有效期
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = "有效期",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                fontFamily = CaiFont,
                                modifier = Modifier.padding(bottom = 8.dp)
                            )
                            OutlinedTextField(
                                value = expirationDate,
                                onValueChange = { newValue ->
                                    // 格式：YYYY-MM-DD
                                    if (newValue.length <= 10) {
                                        expirationDate = newValue
                                        println("AddBankCardScreen - 输入有效期: $newValue") // 日志输出
                                    }
                                },
                                placeholder = {
                                    Text(
                                        text = "YYYY-MM-DD",
                                        fontFamily = CaiFont,
                                        fontSize = 14.sp
                                    )
                                },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = Color(0xFF1E88E5),
                                    focusedLabelColor = Color(0xFF1E88E5)
                                )
                            )
                        }

                        // CVV
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = "CVV安全码",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                fontFamily = CaiFont,
                                modifier = Modifier.padding(bottom = 8.dp)
                            )
                            OutlinedTextField(
                                value = cvv,
                                onValueChange = { newValue ->
                                    // 只允许输入数字，最长4位
                                    if (newValue.isEmpty() || (newValue.all { it.isDigit() } && newValue.length <= 4)) {
                                        cvv = newValue
                                        println("AddBankCardScreen - 输入CVV: $newValue") // 日志输出
                                    }
                                },
                                placeholder = {
                                    Text(
                                        text = "CVV",
                                        fontFamily = CaiFont,
                                        fontSize = 14.sp
                                    )
                                },
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = Color(0xFF1E88E5),
                                    focusedLabelColor = Color(0xFF1E88E5)
                                )
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // 温馨提示
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF3E0)),
                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp)
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.Info,
                            contentDescription = "提示",
                            tint = Color(0xFFFF6F00),
                            modifier = Modifier.size(20.dp)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "温馨提示",
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFFFF6F00),
                            fontFamily = CaiFont
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    Text(
                        text = "• 请确保银行卡信息准确无误",
                        fontSize = 13.sp,
                        color = Color(0xFF5D4037),
                        fontFamily = CaiFont
                    )

                    Spacer(modifier = Modifier.height(4.dp))

                    Text(
                        text = "• 您的银行卡信息将被加密保护",
                        fontSize = 13.sp,
                        color = Color(0xFF5D4037),
                        fontFamily = CaiFont
                    )

                    Spacer(modifier = Modifier.height(4.dp))

                    Text(
                        text = "• 仅用于提现转账，不会用于其他用途",
                        fontSize = 13.sp,
                        color = Color(0xFF5D4037),
                        fontFamily = CaiFont
                    )
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            // 错误提示
            if (viewModel.errorMessage != null) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE))
                ) {
                    Text(
                        text = viewModel.errorMessage!!,
                        color = Color.Red,
                        fontSize = 14.sp,
                        modifier = Modifier.padding(16.dp),
                        fontFamily = CaiFont
                    )
                }
                Spacer(modifier = Modifier.height(16.dp))
            }

            // 成功提示
            if (viewModel.successMessage != null) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9))
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.CheckCircle,
                            contentDescription = "成功",
                            tint = Color(0xFF4CAF50),
                            modifier = Modifier.size(24.dp)
                        )
                        Spacer(modifier = Modifier.width(12.dp))
                        Text(
                            text = viewModel.successMessage!!,
                            color = Color(0xFF4CAF50),
                            fontSize = 14.sp,
                            fontFamily = CaiFont
                        )
                    }
                }
                Spacer(modifier = Modifier.height(16.dp))
            }

            // 提交按钮
            Button(
                onClick = {
                    println("AddBankCardScreen - 点击添加银行卡按钮") // 日志输出
                    viewModel.addBankCard(
                        cardNumber = cardNumber,
                        cardHolderName = cardHolderName,
                        expirationDate = expirationDate,
                        cvv = cvv,
                        bankName = bankName
                    )
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
                    .height(50.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF1E88E5)
                ),
                enabled = !viewModel.isLoading &&
                        cardNumber.isNotEmpty() &&
                        cardHolderName.isNotEmpty() &&
                        expirationDate.isNotEmpty() &&
                        bankName.isNotEmpty(),
                shape = RoundedCornerShape(8.dp)
            ) {
                if (viewModel.isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                }
                Text(
                    text = if (viewModel.isLoading) "添加中..." else "添加银行卡",
                    fontFamily = CaiFont,
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp
                )
            }

            Spacer(modifier = Modifier.height(32.dp))
        }
    }
}